import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LogIn, UserPlus, X, Crown, Sparkles, FolderOpen, Box, MessageSquare, TrendingUp } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

export type PromptType = 'auth' | 'project-limit' | 'rig-limit' | 'ai-message-limit';

interface SavePromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promptType: PromptType;
  onContinueWithoutSaving?: () => void;
  maxProjects?: number;
  maxRigsPerProject?: number;
  maxAiMessages?: number;
  currentCount?: number;
}

export function SavePromptDialog({
  open,
  onOpenChange,
  promptType,
  onContinueWithoutSaving,
  maxProjects = 1,
  maxRigsPerProject = 1,
  maxAiMessages = 5,
  currentCount,
}: SavePromptDialogProps) {
  const navigate = useNavigate();
  const currentPath = window.location.pathname;

  const handleSignUp = () => {
    onOpenChange(false);
    navigate(`/auth?mode=signup&returnTo=${encodeURIComponent(currentPath)}`);
  };

  const handleLogIn = () => {
    onOpenChange(false);
    navigate(`/auth?mode=login&returnTo=${encodeURIComponent(currentPath)}`);
  };

  const handleContinue = () => {
    onOpenChange(false);
    onContinueWithoutSaving?.();
  };

  const handleUpgrade = () => {
    onOpenChange(false);
    navigate('/pricing');
  };

  const getContent = () => {
    switch (promptType) {
      case 'auth':
        return {
          icon: UserPlus,
          iconBg: 'bg-primary/10',
          iconColor: 'text-primary',
          title: 'Create an Account to Save',
          description: 'Sign up or log in to save your rig configuration and access it from anywhere.',
          showAuthButtons: true,
          showUpgrade: false,
          limit: 0,
          current: 0,
          unit: '',
        };
      case 'project-limit':
        return {
          icon: FolderOpen,
          iconBg: 'bg-amber-500/10',
          iconColor: 'text-amber-500',
          title: 'Project Limit Reached',
          description: `You've used all ${maxProjects} project${maxProjects === 1 ? '' : 's'} in your plan. Upgrade to create more projects and organize your work.`,
          showAuthButtons: false,
          showUpgrade: true,
          limit: maxProjects,
          current: currentCount ?? maxProjects,
          unit: 'project',
        };
      case 'rig-limit':
        return {
          icon: Box,
          iconBg: 'bg-blue-500/10',
          iconColor: 'text-blue-500',
          title: 'Rig Limit Reached',
          description: `You've created ${maxRigsPerProject} rig${maxRigsPerProject === 1 ? '' : 's'} in this project. Upgrade to add more rigs per project.`,
          showAuthButtons: false,
          showUpgrade: true,
          limit: maxRigsPerProject,
          current: currentCount ?? maxRigsPerProject,
          unit: 'rig',
        };
      case 'ai-message-limit':
        return {
          icon: MessageSquare,
          iconBg: 'bg-purple-500/10',
          iconColor: 'text-purple-500',
          title: 'AI Message Limit Reached',
          description: `You've used all ${maxAiMessages} AI messages for this period. Upgrade for more AI-powered assistance.`,
          showAuthButtons: false,
          showUpgrade: true,
          limit: maxAiMessages,
          current: currentCount ?? maxAiMessages,
          unit: 'message',
        };
    }
  };

  const content = getContent();
  const Icon = content.icon;
  const progressPercent = content.limit > 0 ? Math.min((content.current / content.limit) * 100, 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center sm:text-center">
          <div className={cn("mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4", content.iconBg)}>
            <Icon className={cn("w-7 h-7", content.iconColor)} />
          </div>
          <DialogTitle className="text-xl">{content.title}</DialogTitle>
          <DialogDescription className="text-base">{content.description}</DialogDescription>
        </DialogHeader>

        {/* Usage Progress (for limit prompts) */}
        {content.showUpgrade && content.limit > 0 && (
          <div className="mt-4 p-4 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Current Usage</span>
              <span className="text-sm text-muted-foreground">
                {content.current} / {content.limit} {content.unit}{content.limit !== 1 ? 's' : ''}
              </span>
            </div>
            <Progress value={progressPercent} className="h-2" />
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              Pro users get significantly higher limits
            </p>
          </div>
        )}

        <div className="flex flex-col gap-3 mt-4">
          {content.showAuthButtons ? (
            <>
              <Button onClick={handleSignUp} className="w-full gap-2">
                <UserPlus className="w-4 h-4" />
                Sign Up Free
              </Button>
              <Button variant="outline" onClick={handleLogIn} className="w-full gap-2">
                <LogIn className="w-4 h-4" />
                Log In
              </Button>
              {onContinueWithoutSaving && (
                <Button variant="ghost" onClick={handleContinue} className="w-full text-muted-foreground gap-2">
                  <X className="w-4 h-4" />
                  Continue Without Saving
                </Button>
              )}
            </>
          ) : content.showUpgrade ? (
            <>
              <Button onClick={handleUpgrade} className="w-full gap-2">
                <Crown className="w-4 h-4" />
                Upgrade to Pro
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full">
                Maybe Later
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full">
              Close
            </Button>
          )}
        </div>

        {/* Pro benefits hint */}
        {content.showUpgrade && (
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1">
              <Sparkles className="w-3 h-3 text-primary" />
              Unlock unlimited potential with Pro
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
